"use client";

import React from "react";
import type { ToolbarProps } from "./Toolbar/types";
import { TextFormattingGroup } from "./Toolbar/TextFormattingGroup";
import { ListGroup } from "./Toolbar/ListGroup";
import { AlignmentGroup } from "./Toolbar/AlignmentGroup";
import { InsertGroup } from "./Toolbar/InsertGroup";
import { AttachmentsGroup } from "./Toolbar/AttachmentsGroup";
import { HistoryGroup } from "./Toolbar/HistoryGroup";

export type { ToolbarProps } from "./Toolbar/types";

export const Toolbar = React.memo(
  ({
    editor,
    disabled,
    isUploading,
    attachedFilesCount,
    attachedFiles,
    onToggleFormat,
    onSetAlignment,
    onToggleLink,
    onImageUpload,
    onFileAttachment,
    onAttachmentRemove,
    onGoogleDriveClick,
    showGoogleDrive,
  }: ToolbarProps) => {
    if (!editor) return null;

    return (
      <div className="border-b p-2 flex flex-wrap gap-1 bg-muted/30 overflow-x-auto scrollbar-thin">
        {/* Text Formatting */}
        <TextFormattingGroup
          editor={editor}
          disabled={disabled}
          onToggleFormat={onToggleFormat}
        />

        <div className="w-px h-6 bg-border self-center mx-1" />

        {/* Lists */}
        <ListGroup editor={editor} disabled={disabled} onToggleFormat={onToggleFormat} />

        <div className="w-px h-6 bg-border self-center mx-1 flex-shrink-0" />

        {/* Alignment */}
        <AlignmentGroup
          editor={editor}
          disabled={disabled}
          onSetAlignment={onSetAlignment}
        />

        <div className="w-px h-6 bg-border self-center mx-1 flex-shrink-0" />

        {/* Links and Elements + Image Upload */}
        <InsertGroup
          editor={editor}
          disabled={disabled}
          isUploading={isUploading}
          onToggleFormat={onToggleFormat}
          onToggleLink={onToggleLink}
          onImageUpload={onImageUpload}
        />

        {/* File Attachments + Google Drive */}
        <AttachmentsGroup
          disabled={disabled}
          isUploading={isUploading}
          attachedFilesCount={attachedFilesCount}
          attachedFiles={attachedFiles}
          onFileAttachment={onFileAttachment}
          onAttachmentRemove={onAttachmentRemove}
          onGoogleDriveClick={onGoogleDriveClick}
          showGoogleDrive={showGoogleDrive}
        />

        <div className="w-px h-6 bg-border self-center mx-1 flex-shrink-0" />

        {/* Undo/Redo */}
        <HistoryGroup disabled={disabled} onToggleFormat={onToggleFormat} />
      </div>
    );
  }
);

Toolbar.displayName = "Toolbar";
